import { useEffect, useState } from "react";
import { CaretDown, ArrowClockwise } from "@phosphor-icons/react";

const STORAGE_PREFIX = "sws_admin_collapse:";

/**
 * Uniform admin-panel shell — chevron collapse (persisted per section in
 * localStorage) plus a self-contained refresh button that pings an
 * optional onRefresh callback with a spinner + subtle success/error toast.
 *
 * Children stay mounted when collapsed (CSS hidden), so any local drafts
 * (unsaved edits, filters, picker state, etc.) survive open/close cycles.
 */
export default function CollapsibleSection({
  id,
  title,
  icon,
  badge,
  subtitle,
  onRefresh,
  extraActions,
  defaultOpen = true,
  testId,
  children,
}) {
  const storageKey = `${STORAGE_PREFIX}${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === null) return defaultOpen;
      return v === "1";
    } catch {
      return defaultOpen;
    }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [flash, setFlash] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? "1" : "0");
    } catch { /* private-mode ignore */ }
  }, [open, storageKey]);

  const doRefresh = async (e) => {
    e.stopPropagation();
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    setFlash("");
    try {
      await onRefresh();
      setFlash("Refreshed ✓");
    } catch (err) {
      setFlash(`Failed: ${err?.message || "error"}`);
    } finally {
      setRefreshing(false);
      setTimeout(() => setFlash(""), 2500);
    }
  };

  return (
    <section
      data-testid={testId || `collapsible-${id}`}
      data-collapse-open={open ? "1" : "0"}
      className="border border-white/10 bg-[#0F0F0F]"
    >
      <header
        onClick={() => setOpen((o) => !o)}
        role="button"
        aria-expanded={open}
        aria-controls={`collapse-body-${id}`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        data-testid={`collapse-toggle-${id}`}
        className="cursor-pointer flex items-center gap-3 px-5 py-4 hover:bg-white/[0.02] transition-colors select-none"
      >
        <CaretDown
          size={14}
          weight="bold"
          className={`text-white/60 transition-transform ${open ? "" : "-rotate-90"}`}
        />
        {icon}
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-black uppercase text-lg tracking-tight text-white/90 flex items-center gap-3 flex-wrap">
            {title}
            {badge}
          </h2>
          {subtitle && (
            <p className="text-white/45 text-xs uppercase tracking-[0.25em] mt-1">{subtitle}</p>
          )}
        </div>
        <div
          className="flex items-center gap-2 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {flash && (
            <span
              data-testid={`collapse-refresh-flash-${id}`}
              className="text-[10px] uppercase tracking-[0.2em] text-[#FF1B91]"
            >
              {flash}
            </span>
          )}
          {extraActions}
          {onRefresh && (
            <button
              type="button"
              onClick={doRefresh}
              disabled={refreshing}
              data-testid={`collapse-refresh-${id}`}
              title="Refresh this section"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 hover:border-[#FF1B91] text-white/70 hover:text-white disabled:opacity-40 px-2.5 py-1.5"
            >
              <ArrowClockwise
                size={12}
                weight="bold"
                className={refreshing ? "animate-spin" : ""}
              />
              {refreshing ? "…" : "Refresh"}
            </button>
          )}
        </div>
      </header>
      <div
        id={`collapse-body-${id}`}
        data-testid={`collapse-body-${id}`}
        className={open ? "px-5 pb-5 pt-1" : "hidden"}
      >
        {children}
      </div>
    </section>
  );
}
